import { useEffect, useRef } from 'react';
import { pb } from '@/lib/pocketbase';

interface UseHeartbeatOptions {
  userId: string;
  userRole: string;
  isEnabled: boolean;
  onError?: (error: any) => void;
}

export const useHeartbeat = ({ userId, userRole, isEnabled, onError }: UseHeartbeatOptions) => {
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const sendHeartbeat = async () => {
    if (!userId || userRole !== 'tutor') return;

    // Check if user has been active recently (within last 3 minutes)
    const lastActiveTime = localStorage.getItem('lastActiveTime');
    const lastActiveTimestamp = lastActiveTime ? parseInt(lastActiveTime, 10) : Date.now();
    const timeSinceActivity = Date.now() - lastActiveTimestamp;
    const maxInactivityForHeartbeat = 3 * 60 * 1000; // 3 minutes

    // Don't send heartbeat if user hasn't been active recently
    if (timeSinceActivity > maxInactivityForHeartbeat) {
      console.log('Skipping heartbeat - user inactive for', Math.round(timeSinceActivity / 1000), 'seconds');
      return;
    }

    try {
      // Find tutor profile
      const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${userId}"`);
      
      // Find or update online status
      try {
        const statusRecord = await pb.collection('tutor_online_status').getFirstListItem(`tutorId = "${tutorProfile.id}"`);
        
        await pb.collection('tutor_online_status').update(statusRecord.id, {
          isOnline: true,
          lastHeartbeat: new Date().toISOString(),
          lastActivity: new Date(lastActiveTimestamp).toISOString(),
          lastSeen: new Date().toISOString()
        });
      } catch (error: any) {
        if (error?.status === 404) {
          // Create new status record
          await pb.collection('tutor_online_status').create({
            tutorId: tutorProfile.id,
            isOnline: true,
            lastHeartbeat: new Date().toISOString(),
            lastActivity: new Date(lastActiveTimestamp).toISOString(),
            lastSeen: new Date().toISOString()
          });
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Heartbeat error:', error);
      onError?.(error);
    }
  };

  useEffect(() => {
    if (!isEnabled || !userId || userRole !== 'tutor') {
      // Clear interval if disabled
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeat every 30 seconds
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [userId, userRole, isEnabled]);

  return { sendHeartbeat };
};
